import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  CreditCard,
  LogOut,
  Monitor,
  Package,
  PackageCheck,
  Store,
  Tag,
  Truck,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { DesktopShell } from "../../components/desktop/shell.tsx";
import { Badge, Button, Empty, Price, Spinner, fen } from "../../components/ui.tsx";
import { api } from "../../api/client.ts";
import { useIdentities, useLogout, useMe } from "../../api/queries.ts";
import { readIdentity, writePurchaseAs, writeIdentity } from "../../lib/identity.ts";
import { displayName } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 账户概览
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **网页版的 `/me` 不是菜单页，是一块看板。**
 *    菜单已经常驻在左边了（`AccountShell`），右边这块再放一遍列表毫无意义。
 *    所以这里放的是**数**：几单待付款、几单待发货、可提现多少、成交多少 ——
 *    进来一眼就知道今天要处理什么，点数字直达那一页。
 */

const ORDER_TABS: { key: string; label: string; icon: LucideIcon }[] = [
  { key: "pending", label: "待付款", icon: CreditCard },
  { key: "paid", label: "待发货", icon: Package },
  { key: "shipped", label: "待收货", icon: Truck },
  { key: "done", label: "已完成", icon: PackageCheck },
];

/**
 * 身份卡上给每个店铺标的那一小行字。
 *
 * 🔴 **平台标的是「我的店铺」，不是「平台」。**
 *    老板：「第二个不应该是平台，应该是我的店铺，第三个是平台管理」。
 *    陈总的三样东西是 ①我自己买东西 ②**我的店铺** ③平台管理 ——
 *    第二行标成"平台"会跟第三行糊在一起，他会以为那是同一件事的两个入口。
 *
 * ⚠️ 其他几种照旧标身份（经销商 / 分销商 / 供应商）：
 *    那是他在链条上的位置，不会跟别的东西混。
 */
const ORG_LABEL: Record<string, string> = {
  platform: "我的店铺",
  supplier: "供应商",
  dealer: "经销商",
  distributor: "分销商",
};

function orgGet<T>(path: string, orgId: number) {
  return fetch(`/api/merchant${path}`, {
    credentials: "include",
    headers: { "X-Org-Id": String(orgId) },
  }).then(async (r) => {
    if (!r.ok) throw new Error((await r.json().catch(() => null))?.message ?? "请求失败");
    return r.json() as Promise<T>;
  });
}

interface LevelStatus {
  current: { name: string } | null;
  stats: { totalGmv: number };
}
interface Balance {
  withdrawable: number;
  pending: number;
}
interface ShopSelf {
  id: number;
  name: string;
  displayName: string;
  status: string;
  orderCount: number;
  since: string;
  contactPhone: string;
}

/** 一块数字看板。⚠️ 数字排在上、说明在下 —— 扫的时候先看到数。 */
function Stat({
  label,
  children,
  hint,
  to,
  search,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  to?: string;
  search?: Record<string, unknown>;
}) {
  const inner = (
    <>
      <p className="text-[13px] text-muted-foreground">{label}</p>
      <p className="mt-1.5 text-[26px] leading-none font-medium">{children}</p>
      {hint && <p className="mt-1.5 text-[12px] text-muted-foreground">{hint}</p>}
    </>
  );
  const cls =
    "rounded-xl border border-border bg-card p-5 transition-colors" +
    (to ? " hover:border-brand-200" : "");
  return to ? (
    <Link to={to} search={(search ?? {}) as never} className={`block ${cls}`}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

/** 经营者身份下的概览。 */
function MerchantOverview({ org }: { org: { id: number; name: string; type: string; status: string } }) {
  const type = org.type;
  /**
   * ⚠️ 权限按服务端的实际名单来（见 `components/desktop/account-nav.tsx` 的说明）：
   *    `/shop`、`/balance` 是 `anyMerchant`、`/level` 是 `resellerOnly` ——
   *    平台身份查这几个都会 403，别发这些请求，也别摆出对应的卡片。
   */
  const canPrice =
    org.type === "platform" || org.type === "dealer" || org.type === "distributor";
  const canRecruit = org.type === "dealer" || org.type === "distributor";
  const hasOwnShop =
    org.type === "supplier" || org.type === "dealer" || org.type === "distributor";

  const shop = useQuery({
    queryKey: ["merchant", "shop", org.id],
    queryFn: () => orgGet<ShopSelf>("/shop", org.id),
    enabled: hasOwnShop,
  });
  const balance = useQuery({
    queryKey: ["merchant", org.id, "balance"],
    queryFn: () => orgGet<Balance>("/balance", org.id),
    enabled: hasOwnShop,
  });
  const level = useQuery({
    queryKey: ["merchant", org.id, "level"],
    queryFn: () => orgGet<LevelStatus>("/level", org.id),
    enabled: canRecruit,
  });

  const days = shop.data
    ? Math.max(1, Math.round((Date.now() - new Date(shop.data.since).getTime()) / 86_400_000))
    : 0;

  return (
    <div className="space-y-5">
      {/* ── 店铺抬头 ── */}
      <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-5">
        <span className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-brand-50">
          <Store className="size-6 text-brand-600" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[18px] font-medium">{shop.data?.displayName || org.name}</p>
          <p className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary">{ORG_LABEL[org.type] ?? org.type}</Badge>
            {level.data?.current && <Badge variant="brand">{level.data.current.name}</Badge>}
            {org.status === "pending" && <Badge variant="warning">待审核</Badge>}
            {org.status === "suspended" && <Badge variant="destructive">已停业</Badge>}
          </p>
        </div>
        {shop.data && (
          <p className="shrink-0 text-right text-[13px] text-muted-foreground">
            成交 {shop.data.orderCount} 单
            <br />
            开店 {days} 天
          </p>
        )}
      </div>

      {/*
        🔴 **没填联系方式会直接丢单**：线下收款，买家下完单要联系你付款。
           安静地空着的话商家永远不会意识到问题出在这里，所以主动顶到最上面。
      */}
      {shop.data && !shop.data.contactPhone?.trim() && (
        <Link
          to="/shop-settings"
          search={{ orgId: org.id } as never}
          className="block rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-[14px] leading-relaxed text-amber-800 transition-colors hover:bg-amber-100/70"
        >
          还没填收款联系方式 —— 客户下单后不知道该找谁付款，这单大概率就丢了。去填 →
        </Link>
      )}

      {/* ── 数字 ── */}
      <div className="grid grid-cols-3 gap-4">
        {hasOwnShop && (
          <Stat
            label="可提现"
            hint={balance.data ? `待结算 ${fen(balance.data.pending)}` : undefined}
            to="/withdraw"
            search={{ orgId: org.id }}
          >
            {balance.data ? (
              <Price cents={balance.data.withdrawable} className="text-[26px] text-brand-600" />
            ) : (
              "—"
            )}
          </Stat>
        )}
        {canRecruit && (
          <Stat label="累计成交" hint={level.data?.current?.name ?? undefined}>
            {level.data ? <Price cents={level.data.stats.totalGmv} className="text-[26px]" /> : "—"}
          </Stat>
        )}
        {/* ⚠️ 成交单数来自 `/shop`，平台查不了那个接口 —— 别摆一个永远是「—」的格子。 */}
        {hasOwnShop && (
          <Stat
            label="客户订单"
            hint="客户在我店里下的单"
            to="/seller-orders"
            search={{ orgId: org.id }}
          >
            {shop.data?.orderCount ?? "—"}
          </Stat>
        )}
      </div>

      {/*
        ⚠️ 这里原来有一张「平台后台」的卡 —— 已经删了。
           后台是**第三个身份**（左边切换器里那个胶囊），不是店铺概览的一块内容。
           老板：「这是三个东西，不要混在一起」。
      */}

      {/* ── 常用动作 ── */}
      {/* ⚠️ 只放"每天都要点的那几个"，全部入口在左边导航里 —— 这里再列一遍就成了两份菜单。 */}
      <div className="grid grid-cols-4 gap-4">
        {canPrice && (
          <Link
            to="/my-pricing"
            search={{ orgId: org.id } as never}
            className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-4 py-5 transition-colors hover:border-brand-200"
          >
            <Tag className="size-6 text-brand-600" />
            <span className="text-[14px]">我的定价</span>
          </Link>
        )}
        <Link
          to="/ship"
          search={{ orgId: org.id } as never}
          className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-4 py-5 transition-colors hover:border-brand-200"
        >
          <Truck className="size-6 text-brand-600" />
          <span className="text-[14px]">要我发的货</span>
        </Link>
        {canRecruit && (
          <Link
            to="/team"
            search={{ orgId: org.id } as never}
            className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-4 py-5 transition-colors hover:border-brand-200"
          >
            <Users className="size-6 text-brand-600" />
            <span className="text-[14px]">团队与客户</span>
          </Link>
        )}
        {/*
          ⚠️ 这里原来有一个「进货」——已经删了。
             进货是**「我自己」**要做的事（我这个人买东西），不是这家店的经营动作。
             入口在切换器切到「我自己」之后的「去逛商城」。
        */}
      </div>
    </div>
  );
}

export function DesktopMe() {
  const navigate = useNavigate();
  const { data: me, isPending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const logout = useLogout();

  /**
   * 🔴 **所有 hook 必须排在 early return 之前** —— 这个坑本项目踩过一次：
   *    未登录那一轮少跑几个 hook，登录后又多出来，React 直接整页白屏。
   */
  const [identity, setIdentityState] = useState<string | null>(readIdentity);
  const orgs = identities?.orgs ?? [];
  const activeOrg = orgs.find((o) => String(o.id) === identity) ?? null;

  const unreadQ = useQuery({
    queryKey: ["messages", "unread"],
    queryFn: () => api.get<{ count: number }>("/me/messages/unread"),
    enabled: Boolean(me),
    staleTime: 30_000,
  });

  /** ⚠️ 左导航切换身份写的是 localStorage，这一页要跟着换内容。 */
  useEffect(() => {
    const onChange = () => setIdentityState(readIdentity());
    window.addEventListener("zx:identity-changed", onChange);
    return () => window.removeEventListener("zx:identity-changed", onChange);
  }, []);

  if (isPending)
    return (
      <DesktopShell>
        <Spinner compact />
      </DesktopShell>
    );

  if (!me)
    return (
      <DesktopShell>
        <Empty
          title="还没登录"
          hint="登录后可以下单、查看订单、管理收货地址"
          action={{ label: "去登录", to: "/login" }} compact />
      </DesktopShell>
    );

  const name = displayName(me);
  const myShop = identities?.binding ?? null;
  const unread = unreadQ.data?.count ?? 0;

  return (
    <AccountShell title={activeOrg ? "经营概览" : "账户概览"}>
      {activeOrg ? (
        <MerchantOverview org={activeOrg} />
      ) : (
        <div className="space-y-5">
          {/* ── 本人抬头 ── */}
          <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-5">
            <span className="flex size-14 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[22px] font-medium text-brand-700">
              {name.slice(0, 1)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[18px] font-medium">{name}</p>
              <p className="mt-0.5 text-[13px] text-muted-foreground">{me.phone ?? me.email}</p>
            </div>
            {unread > 0 && (
              <Link
                to="/messages"
                className="shrink-0 rounded-lg bg-destructive/10 px-3 py-2 text-[13px] text-destructive transition-colors hover:bg-destructive/15"
              >
                {unread} 条未读消息
              </Link>
            )}
          </div>

          {/*
            🔴 **我的专属服务商。**
               这是这个平台跟普通商城最不一样的一条：买家有一个固定的服务商，
               价格按他给你算、售后找他。看不到的话用户根本不知道有这回事。
          */}
          {myShop && (
            <Link
              to="/shop/$id"
              params={{ id: String(myShop.id) }}
              className="flex items-center gap-4 rounded-xl border border-brand-200 bg-brand-50 p-5 transition-colors hover:bg-brand-100/60"
            >
              <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-brand-600">
                <Store className="size-5 text-white" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-medium text-brand-700">{myShop.name}</p>
                <p className="mt-0.5 text-[13px] text-brand-600/80">
                  你的专属服务商 · 他的店里比平台价便宜，售后也找他
                </p>
              </div>
            </Link>
          )}

          {/*
            🔴 **订单状态做成四个直达入口。**
               用户来这一页十次有八次是想看"我那单发了没"。
          */}
          <div>
            <p className="mb-3 text-[15px] font-medium">我的订单</p>
            <div className="grid grid-cols-4 gap-4">
              {ORDER_TABS.map((t) => (
                <Link
                  key={t.key}
                  to="/orders"
                  search={{ status: t.key } as never}
                  className="flex flex-col items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-6 transition-colors hover:border-brand-200"
                >
                  <t.icon className="size-7 text-brand-600" />
                  <span className="text-[14px]">{t.label}</span>
                </Link>
              ))}
            </div>
          </div>

          <Button
            variant="outline"
            loading={logout.isPending}
            onClick={async () => {
              await logout.mutateAsync();
              /**
               * ⚠️ **退出时把身份一起清掉。**
               *    不清的话，下一个人在这台电脑上登录，会带着上一个人的
               *    采购身份逛商城 —— 价格是别人的进货价。
               */
              writeIdentity("");
              writePurchaseAs(null);
              /**
               * ⚠️ **退出之后落到登录页，不是首页。**
               *    退出登录这个动作的下一步几乎总是"换个号登进来"或者"我不想让别人
               *    用我的号" —— 甩回首页等于让他自己再去找登录入口，
               *    而首页上那个入口在网页版顶栏最右边、手机版藏在「我的」里。
               */
              navigate({ to: "/login" });
            }}
          >
            <LogOut className="size-4" />
            退出登录
          </Button>
        </div>
      )}
    </AccountShell>
  );
}
